/**
 * The Configure page's label printer settings, as data rather than as form plumbing.
 *
 * These settings are per cashpoint. That is not a detail: 21 counters each carry their own
 * printer queue, and a save that forgets which counter it is editing overwrites another
 * shop's printer with no visible symptom until someone there prints a label.
 */

/** One stock the backend will accept, with the dimensions that make the choice meaningful. */
export interface LabelStockOption {
    stockId: string;
    name: string;
    acrossHeadMm: number;
    feedMm: number;
}

/** What `GET /configuration/label-printer/:cashPointId` returns. */
export interface LabelPrinterConfigPayload {
    cashPointId: number;
    queue: string;
    stockId: string | null;
    availableStock: LabelStockOption[];
}

/** The two fields the Printers tab edits for the selected cashpoint. */
export interface LabelPrinterFormValues {
    labelQueue: string;
    labelStockId: string | undefined;
}

/**
 * Option label for the stock select.
 *
 * The SKU alone is unreadable — `30373` and `11352` differ by 3.3 mm along the feed, which is
 * the entire difference between one label and two — so the dimensions are part of the choice
 * rather than something to look up elsewhere.
 */
export function describeLabelStockOption(option: LabelStockOption): string {
    return `${option.stockId} — ${option.name} (${option.acrossHeadMm} × ${option.feedMm} mm)`;
}

/**
 * Server payload to form values.
 *
 * `null` becomes `undefined` because Ant Design's `Select` shows its placeholder for
 * `undefined` and an empty selected item for `null`, and "no stock configured" should read
 * as unset rather than as a blank choice.
 */
export function toLabelPrinterFormValues(
    payload: Pick<LabelPrinterConfigPayload, 'queue' | 'stockId'>,
): LabelPrinterFormValues {
    return {
        labelQueue: payload.queue,
        labelStockId: payload.stockId ?? undefined,
    };
}

/**
 * Form values to request body, normalised the same way the backend will normalise them.
 *
 * Trimming here means the value the operator sees after reopening the page is the value that
 * was saved, rather than one that differs by whitespace the server quietly removed.
 */
export function toLabelPrinterRequestBody(values: LabelPrinterFormValues): {
    queue: string;
    stockId: string | null;
} {
    const stockId = values.labelStockId?.trim() ?? '';

    return {
        queue: values.labelQueue?.trim() ?? '',
        stockId: stockId === '' ? null : stockId,
    };
}
